import React, { useEffect, useRef } from 'react';
import { Heading, HeadingHierarchy } from '../../Heading/Heading';
import extendClassName from '../../../../lib/bemUtils/bem';
import { BtnModifier } from '../../../../components/molecules/Btn';
import { Button } from '../../../../components/molecules/Btn/Button';
import { Icons } from '../../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';

type AttachedFilesPreviewListProps = {
  selection: File[];
  hierarchy: HeadingHierarchy;
  buttonDisabled?: boolean;
  focusLastElement?: boolean;
  onClick: (item: File, index: number) => void;
};

export function AttachedFilesPreviewList({
  selection,
  hierarchy,
  buttonDisabled,
  focusLastElement,
  onClick,
}: AttachedFilesPreviewListProps) {
  const { t } = useSonTranslation('FileUpload');
  const ulRef = useRef<HTMLUListElement>(null);
  const id = useHtmlValidId();

  useEffect(() => {
    if (ulRef.current && focusLastElement) {
      Array.from(ulRef.current.getElementsByTagName('button')).at(-1)?.focus();
    }
  }, [focusLastElement, selection]);

  return (
    <>
      <Heading
        hierarchy={hierarchy}
        className={extendClassName('attached-files-base__headline', 'list') + ' font-bold-15-19'}
        id={`attached-files-list-headline-${id}`}
      >
        {t('selectedFiles')}
      </Heading>
      {/* Since selection and selectedFiles are the same, ul is not empty */}
      <ul
        className="attached-files-base__preview-list font-regular-14-19"
        aria-labelledby={`attached-files-list-headline-${id}`}
        ref={ulRef}
      >
        {selection.map((file, index) => {
          return (
            <li key={file.name} className="attached-files-base__preview-list-element">
              <div className="attached-files-base__preview-list-file-name">{file.name}</div>
              <div className="attached-files-base__preview-list-button">
                <Button
                  type="button"
                  title={t('deleteButtonText', { fileName: file.name })}
                  disabled={buttonDisabled}
                  tooltipPlacement={TooltipPlacement.TOP}
                  hideTooltipFromScreenReader
                  modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
                  onClick={() => onClick(file, index)}
                >
                  <Button.Icon
                    svgSpriteId={Icons.DELETE_X_SMALL}
                    text={t('deleteButtonText', { fileName: file.name })}
                  />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
